import { Component, EventEmitter, OnInit, Output } from '@angular/core';
// Models
import { MetricsToolbarFilters } from '../../model/metrics-toolbar.model';
import { OrganizationModel } from '../../model/organization.model';
import { ProductModel } from '../../model/products.model';

//utils
import { DateUtils } from '@cv-invision/core';

import { OrganizationsService } from '../../service/organizations.service';
import {
    catchError,
    debounceTime,
    lastValueFrom,
    map,
    of,
    Subject,
    Subscription,
    switchMap,
    takeUntil,
} from 'rxjs';
import { ProductsService } from '../../service/products.service';
import { MenuItem } from 'primeng/api';
import { MetricsViewModeEnum } from './enum/metrics-toolbar.enum';


@Component({
    selector: 'cv-metrics-toolbar',
    templateUrl: 'metrics-toolbar.component.html',
    styleUrls: ['metrics-toolbar.component.scss'],
})
export class MetricsToolbarComponent implements OnInit {
    @Output() filtersChange = new EventEmitter<MetricsToolbarFilters>();
    @Output() viewMode = new EventEmitter<MetricsViewModeEnum>();
    private atComponentDestroy = new Subject();
    public filters: MetricsToolbarFilters = {
        dateRange: {
            startDate: '',
            endDate: '',
        },
        organizations: [],
        products: [],
    };

    // - Organization Filter
    public organizationOptions: OrganizationModel[] = [];
    public selectedOrganizations: any[] = [];
    private orgFilterSubject = new Subject<string>();
    private orgFilterSub: Subscription;

    // - Products Filter
    public productOptions: ProductModel[] = [];
    public selectedProducts: any[] = [];
    private productFilterSubject = new Subject<string>();
    private productFilterSub: Subscription;

    // - Tab Menu
    public metricsTabMenuItems: MenuItem[] = [];
    public activeTabItem: MenuItem;

    // Prime NG - p-calendar
    public selectedDatesRange: Date[] = []; // Used by p-calendar

    constructor(
        private organizationService: OrganizationsService,
        private productService: ProductsService,
    ) {}

    async ngOnInit() {
        await this.loadInitialData();
        this.setCurrentWeekRange();

        this.orgFilterSubject
            .pipe(
                takeUntil(this.atComponentDestroy),
                debounceTime(1000),
                switchMap((term) =>
                    this.organizationService.getOrganizations(term).pipe(catchError(() => of([]))),
                ),
            )
            .subscribe((mergedOrganizations) => {
                setTimeout(() => {
                    this.organizationOptions = mergedOrganizations;
                    this.areAllOrganizationsSelected();
                });
            });

        this.productFilterSubject
            .pipe(
                takeUntil(this.atComponentDestroy),
                debounceTime(1000),
                switchMap((term) =>
                    this.productService
                        .getProductsByMultipleTrackings(term)
                        .pipe(catchError(() => of([]))),
                ),
            )
            .subscribe((mergedProducts) => {
                setTimeout(() => {
                    this.productOptions = mergedProducts;
                    this.areAllProductsSelected();
                });
            });
    }

    ngOnDestroy(): void {
        this.atComponentDestroy.next({});
        this.atComponentDestroy.unsubscribe();
    }

    public setCurrentWeekRange(): void {
        this.selectedDatesRange = DateUtils.getDateRangeFromToday(7);
        this.triggerDateChange(this.selectedDatesRange);
    }

    public setCustomTodayRange(): void {
        this.selectedDatesRange = DateUtils.getDateRangeFromToday(0);
        this.triggerDateChange(this.selectedDatesRange);
    }

    public triggerDateChange(dates: Date[]) {
        if (dates && dates.length === 2 && dates[0] && dates[1]) {
            const start = DateUtils.formatDateForApi(dates[0]);
            const end = DateUtils.formatDateForApi(dates[1], true);

            if (start && end) {
                this.filters.dateRange.startDate = start;
                this.filters.dateRange.endDate = end;
                this.emitFilters();
            }
        }
    }

    public emitFilters() {
        this.filtersChange.emit({ ...this.filters });
    }

    /**
     * Loads the initial component data.
     * @returns {Promise<void>}
     */
    public async loadInitialData(): Promise<void> {
        this.buildTabMenu();
        await this.loadOrganizationsOptions();
        await this.loadProductOptions();
    }

    /**
     * Loads the list of organizations for the multiselect dropdown.
     *
     * @returns {Promise<void>}
     */
    public async loadOrganizationsOptions() {
        try {
            this.organizationOptions = await lastValueFrom(
                this.organizationService.getOrganizations(),
            );
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Loads the list of products for the multiselect dropdown.
     *
     * @returns {Promise<void>}
     */
    public async loadProductOptions() {
        try {
            this.productOptions = await lastValueFrom(
                this.productService.getProductsByMultipleTrackings(),
            );
        } catch (error) {
            console.error(error);
        }
    }
    /**
     * Applies a text filter to the products options.
     *
     * @param event The input event containing the filter text.
     * @returns {void}
     */
    onProductFilter(event: any) {
        const text = event.target.value ?? '';
        this.productFilterSubject.next(text);
    }
    /**
     * Handles changes to the selected products.
     *
     * @param selected The new selection event object.
     * @returns {void}
     */
    onProductsChange(selected: any) {
        const selectedProducts = selected?.value || [];
        this.selectedProducts = selectedProducts;
        this.filters.products = selectedProducts;
    }
    /**
     * Toggles selection of all product options.
     *
     * @returns {void}
     */
    toggleSelectAllProducts() {
        if (this.areAllProductsSelected()) {
            this.selectedProducts = [];
        } else {
            this.selectedProducts = this.productOptions.map((opt) => opt.id);
        }
        this.filters.products = this.selectedProducts;
    }
    /**
     * Determines whether all product options are currently selected.
     *
     * @returns {boolean} True if all products are selected; otherwise false.
     */
    areAllProductsSelected(): boolean {
        return (
            this.productOptions.length > 0 &&
            this.selectedProducts.length === this.productOptions.length
        );
    }
    /**
     * Applies a text filter to the organizations options.
     *
     * @param event The input event containing the filter text.
     * @returns {void}
     */
    onOrganizationFilter(event: any) {
        const text = event.target.value ?? '';
        this.orgFilterSubject.next(text);
    }
    /**
     * Handles changes to the selected organizations.
     *
     * @param selected The new selection event object.
     * @returns {void}
     */
    onOrganizationsChange(selected: any) {
        const selectedOrganizations = selected?.value || [];
        this.selectedOrganizations = selectedOrganizations;
        this.filters.organizations = selectedOrganizations;
    }

    /**
     * Toggles selection of all organization options.
     *
     * @returns {void}
     */
    toggleSelectAllOrganizations() {
        if (this.areAllOrganizationsSelected()) {
            this.selectedOrganizations = [];
        } else {
            this.selectedOrganizations = this.organizationOptions.map((opt) => opt.id);
        }
        this.filters.organizations = this.selectedOrganizations;
    }
    /**
     * Toggles selection of all organization options.
     *
     * @returns {void}
     */
    areAllOrganizationsSelected(): boolean {
        return (
            this.organizationOptions.length > 0 &&
            this.selectedOrganizations.length === this.organizationOptions.length
        );
    }

    /**
     * Validates if grid mode is selected
     * @returns {boolean}
     */
    isGridSelected(): boolean {
        return this.activeTabItem.id === MetricsViewModeEnum.GRID;
    }

    /**
     * Builds the tab menu for the toolbar section.
     * @returns {void}
     */
    buildTabMenu(): void {
        this.metricsTabMenuItems = [
            {
                label: 'Grid',
                id: MetricsViewModeEnum.GRID,
                icon: 'fa-regular fa-table',
                command: () => this.setTabItem(MetricsViewModeEnum.GRID),
            },
            {
                label: 'Graphs',
                id: MetricsViewModeEnum.GRAPH,
                icon: 'fa-regular fa-chart-bar',
                command: () => this.setTabItem(MetricsViewModeEnum.GRAPH),
            },
        ];

        this.activeTabItem = this.metricsTabMenuItems[0];
        this.viewMode.emit(this.metricsTabMenuItems[0].id as MetricsViewModeEnum);
    }

    /**
     * Sets the tab selected item 
     * @param {MetricsViewModeEnum} mode - The mode item identifier.
     * @returns {void}
     */
    private setTabItem(mode: MetricsViewModeEnum): void {
        this.activeTabItem =
            this.metricsTabMenuItems.find((item) => item.id === mode) ??
            this.metricsTabMenuItems[0];
        this.viewMode.emit(mode);
    }
}
